// Shared API host resolver for CDN mode
// Resolves API base URL by sending visit/track to candidate subdomains (e.g., api.example.com)
// Exposes: ready (Promise), visitReady (Promise), ensureVisit(), getApiBaseUrl(), buildApiUrl(path), hasApi()

import { trackGoal } from './metrika';

// Subdomain to use for API host (e.g., www.example.com → api.example.com)
const API_SUBDOMAIN = 'api';
// Frontend subdomain prefix to replace with API_SUBDOMAIN (e.g., www → api)
const FRONTEND_PREFIX = 'www';
// B24-92552: visitId обязателен — без него теряются события трекинга и привязка Banki.ru.
// Одной попытки мало: ответ 200 с visitId: null раньше считался успехом, и вся страница
// дальше слала события с visit_id: null.
const ATTEMPT_TIMEOUT_MS = 5000;
// Общий бюджет имеет приоритет над числом попыток: истёк — поток завершается,
// сколько бы попыток ни осталось. Иначе клиент ждёт до ~28 секунд.
const VISIT_BUDGET_MS = 15000;
const MAX_VISIT_ATTEMPTS = 4;
const BACKOFF_MS = [300, 800, 2000, 5000];
// Явный 503 означает, что визита сейчас не будет: не долбим, ждём дольше и только один раз.
const UNAVAILABLE_DELAY_MS = 5000;
let apiBaseUrl = null;
let apiResolved = false;
let inFlightVisit = null;
let failureReported = false;

// Резолвится первым валидным visitId — когда бы он ни пришёл: стартовым потоком,
// повтором по `online` или ленивым ensureVisit(). Снимок стартового потока тут не годится:
// он застыл бы на null ровно в том сценарии восстановления, ради которого всё делается.
// Если визита нет вовсе, промис не резолвится — ждать его нужно через
// Promise.race([visitReady, timeout]), а не голым await.
let resolveVisitReady = null;
export const visitReady = new Promise((resolve) => { resolveVisitReady = resolve; });

function isValidVisitId(value) {
  return Number.isInteger(value) && value > 0;
}

function hasVisit() {
  return isValidVisitId(window.__visitId);
}

// Разброс, чтобы клиенты не возвращались к упавшему бэкенду синхронной волной.
function withJitter(ms) {
  return Math.round(ms * (0.7 + Math.random() * 0.6));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildApiHostname(hostname) {
  if (hostname.startsWith(FRONTEND_PREFIX + '.')) {
    return API_SUBDOMAIN + '.' + hostname.slice(FRONTEND_PREFIX.length + 1);
  }
  return `${API_SUBDOMAIN}.${hostname}`;
}

function buildCandidateBases() {
  const protocol = window.location.protocol || 'https:';
  const hostWithPort = window.location.host; // includes port in dev
  const hostname = window.location.hostname; // bare hostname for CDN
  const sameOrigin = `${protocol}//${hostWithPort}`;
  const apiHost = buildApiHostname(hostname);
  const cdnHosts = [`${protocol}//${apiHost}`];
  // In localhost/dev, prefer same-origin so MSW SW can intercept requests.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return [sameOrigin, ...cdnHosts];
  }
  return cdnHosts;
}

function isLocalhost() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost');
}

// Раскладывает ответ visit/track по window.* — вынесено из resolveAndTrack,
// потому что вызывается на каждой попытке, а не один раз.
function applyVisitResponse(base, data) {
  apiBaseUrl = base;
  apiResolved = true;
  window.__apiBaseUrl = apiBaseUrl;
  if (data && typeof data.visitId !== 'undefined') {
    window.__visitId = data.visitId;
  }
  if (data && typeof data.offerVisitIds !== 'undefined') {
    window.__visitData = Object.assign({}, window.__visitData || {}, { offerVisitIds: data.offerVisitIds });
    applyOfferVisitIds(data.offerVisitIds);
  }
  if (data && typeof data.serverTime === 'number' && data.serverTime > 0) {
    window.__serverTime = data.serverTime;
  }
  if (data && typeof data.bankiRuInRollout !== 'undefined') {
    window.__bankiRuInRollout = Boolean(data.bankiRuInRollout);
  }
  if (hasVisit() && resolveVisitReady) {
    resolveVisitReady(window.__visitId);
    resolveVisitReady = null;
  }
  const { name, amount, restamount, mfoscore, fullamount } = data || {};
  const fields = { name: name ?? null, amount: typeof amount === 'number' ? amount : (amount ? Number(amount) : null), restamount: typeof restamount === 'number' ? restamount : (restamount ? Number(restamount) : null), mfoscore: typeof mfoscore === 'number' ? mfoscore : (mfoscore ? Number(mfoscore) : null), fullamount: typeof fullamount === 'number' ? fullamount : (fullamount ? Number(fullamount) : null) };
  window.__visitData = Object.assign({}, window.__visitData || {}, { fields });
  try {
    // Событие отправляется и без визита — намеренно: его слушатели (персонализация
    // заголовка в tzaem/cash2you/monetka) используют только fields, а поля приходят
    // независимо от того, выдан ли visitId. Замолчать здесь — потерять персонализацию.
    // Контракт для новых потребителей: detail.visitId может быть null, признак —
    // detail.hasVisit. Кому нужен гарантированный визит, ждёт visitReady/ensureVisit(),
    // а не это событие.
    const evt = new CustomEvent('visit:tracked', { detail: { visitId: hasVisit() ? window.__visitId : null, hasVisit: hasVisit(), offerVisitIds: window.__visitData.offerVisitIds, fields, serverTime: window.__serverTime ?? null, bankiRuInRollout: window.__bankiRuInRollout ?? null } });
    window.dispatchEvent(evt);
    document.dispatchEvent(evt);
  } catch (_) {}
}

// Одна попытка по одному кандидату. Возвращает исход, решение о ретрае принимает вызывающий.
async function attemptVisitTrack(base, body, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${base}/api/visit/track`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
    });
    clearTimeout(timer);

    // Класс статуса разбираем ДО тела: nginx на деплое или при аварии отдаёт
    // 502/503 HTML-страницей, и если сначала пытаться распарсить JSON, такой ответ
    // выглядит как «чужой хост» и вычёркивается без единого повтора — то есть
    // самый частый сбой апстрима выпадал бы из ретраев.
    // Апстрим сообщил, что визита сейчас не будет.
    if (res.status === 503) return 'unavailable';
    // Прочие серверные ошибки — повторяем.
    if (res.status >= 500) return 'retry';
    // Клиентская ошибка — повтор не поможет.
    if (res.status >= 400) return 'invalid-host';

    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      data = null;
    }

    // Успешный ответ, но не JSON — это не наш API. Витрины за CDN по любому пути
    // отдают закэшированный index.html с кодом 200, такой «успех» принимать нельзя:
    // иначе apiBaseUrl укажет на CDN и туда же уйдут click/send, checker, bankiru/*.
    if (!data || typeof data !== 'object') return 'invalid-host';

    // Хост рабочий (ответил валидным JSON) — фиксируем его независимо от того,
    // выдан ли визит: клики через /api/click/send должны работать в любом случае.
    applyVisitResponse(base, data);

    return hasVisit() ? 'success' : 'retry';
  } catch (_) {
    clearTimeout(timer);
    return 'retry';
  }
}

async function resolveAndTrack() {
  if (apiResolved && hasVisit()) return apiBaseUrl;
  const bases = buildCandidateBases();
  const body = JSON.stringify(collectPagePayload());
  const deadline = Date.now() + VISIT_BUDGET_MS;
  let unavailableRetryUsed = false;
  // Считаем попытки текущего эпизода, а не за всю сессию — иначе в цели Метрики
  // приезжает накопленное число, по которому не видно, за сколько попыток восстановились.
  let attemptsMade = 0;

  for (let attempt = 0; attempt < MAX_VISIT_ATTEMPTS; attempt++) {
    const budgetLeft = deadline - Date.now();
    if (budgetLeft <= 0) break;

    let sawUnavailable = false;
    let sawRetryable = false;

    for (const base of bases) {
      const left = deadline - Date.now();
      if (left <= 0) break;

      attemptsMade++;
      const outcome = await attemptVisitTrack(base, body, Math.min(ATTEMPT_TIMEOUT_MS, left));

      if (outcome === 'success') {
        if (attemptsMade > 1) trackGoal('visit_track_recovered', { attempts: attemptsMade });
        return apiBaseUrl;
      }
      if (outcome === 'unavailable') sawUnavailable = true;
      if (outcome === 'retry') sawRetryable = true;
    }

    if (!sawUnavailable && !sawRetryable) break; // все кандидаты отвалились безнадёжно

    let delay;
    if (sawUnavailable) {
      if (unavailableRetryUsed) break; // на 503 положен ровно один отложенный повтор
      unavailableRetryUsed = true;
      delay = withJitter(UNAVAILABLE_DELAY_MS);
    } else {
      delay = withJitter(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]);
    }

    if (Date.now() + delay >= deadline) break;
    await sleep(delay);
  }

  apiResolved = true;
  if (!apiBaseUrl) {
    apiBaseUrl = null;
    window.__apiBaseUrl = null;
  }
  // Один раз за сессию: иначе каждый неуспешный ensureVisit() задваивал бы метрику.
  if (!failureReported) {
    failureReported = true;
    trackGoal('visit_track_failed', { attempts: attemptsMade, hasApi: apiBaseUrl ? 1 : 0 });
  }
  return apiBaseUrl;
}

// Единственная точка запуска visit/track: пока запрос в полёте, все вызывающие
// присоединяются к нему. Без этого клик или pageshow во время ретраев (бэкофф до 5s)
// стартуют параллельный запрос и создают второй визит на одну загрузку страницы.
export function ensureVisit() {
  if (hasVisit()) return Promise.resolve(window.__visitId);
  if (inFlightVisit) return inFlightVisit;

  inFlightVisit = resolveAndTrack()
    .then(() => (hasVisit() ? window.__visitId : null))
    .finally(() => { inFlightVisit = null; });

  return inFlightVisit;
}

export function getApiBaseUrl() {
  return apiBaseUrl;
}

export function buildApiUrl(path) {
  if (!apiBaseUrl) return null;
  if (!path.startsWith('/')) return `${apiBaseUrl}/${path}`;
  return `${apiBaseUrl}${path}`;
}

export function hasApi() {
  return Boolean(apiBaseUrl);
}

function collectPageOfferIds() {
  const ids = [];
  document.querySelectorAll('[data-offer-id]').forEach((el) => {
    const id = el.dataset.offerId;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

function applyOfferVisitIds(offerVisitIds) {
  if (!offerVisitIds || typeof offerVisitIds !== 'object') return;
  Object.keys(offerVisitIds).forEach((productId) => {
    const el = document.querySelector(`[data-offer-id="${productId}"]`);
    if (el) el.dataset.offerVisitId = String(offerVisitIds[productId]);
  });
}

function collectPagePayload() {
  // Раньше эти параметры (ph, Name:z5g, utm_*, _websiteId, alg и т.д.) читал
  // сервер напрямую из query-строки при рендере страницы. Теперь страница
  // статическая (CDN), поэтому их собирает и пересылает фронтенд —
  // как есть, без повторного декодирования (бэкенд ожидает то же
  // представление значений, что раньше давал $request->query->all()).
  const pageParams = Object.fromEntries(new URLSearchParams(window.location.search || ''));
  return { ...pageParams, idsProducts: collectPageOfferIds() };
}

export const ready = (async () => {
  try {
    if (typeof window !== 'undefined' && window.__mswReadyPromise && isLocalhost()) {
      await window.__mswReadyPromise.catch(() => {});
    }
  } catch (_) {}
  await ensureVisit();
  if (!apiBaseUrl) {
    window.__workOffline = true;
    return null;
  }
  return apiBaseUrl;
})();

// Связь восстановилась, а визита так и нет — пробуем ещё раз.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (hasVisit()) return;
    apiResolved = false;
    ensureVisit();
  });
}
